import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  Grid,
  GridItem,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Image,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import {useLocation, useNavigate} from "react-router-dom";
import { isIOS } from "react-device-detect";
import {FullScreenContext} from "../../contexts/FullScreenContext";

import "../../App.css";
import APMainButton from "../../components/APMainButton";
import { FormattedMessage } from "react-intl";
import APButton from "../../components/APButton";

interface SplashProps {
  sdk: any;
  splash: boolean;
  setSplash: (value: boolean) => void;
}

interface LocationState {instantLogin: boolean, instantRegister: boolean}

export default function SplashScreen(props: SplashProps) {
  const { sdk, splash, setSplash } = props;
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");
  const navigate = useNavigate();
  const location = useLocation();
  const locationState: React.MutableRefObject<LocationState | null> = useRef(location.state as LocationState);

  const { onLogin, onSignUp } = useMemo(() => {
    const onLogin = () => {
      setSplash(false);
      locationState.current = null;

      const observerAppInstance = sdk.getObserverAppInstance()

      if (!sdk.getFirebase().isLoggedIn()) {
        observerAppInstance.trigger("userlogin:show");
      }

      observerAppInstance
        .attach("userlogin:done", async (shouldUpdate: any) => {
          if (sdk.getFirebase().isLoggedIn()) {
            navigate("/game");
          } else {
            setSplash(true);
          }
        });
      observerAppInstance
        .attach("userscreen:transition:login_to_signup", async () => {
          onSignUp()
        })
      observerAppInstance.attach("userscreen:transition:login_to_forgotpassword", async () => onForgotPassword());
    }

    const onSignUp = () => {
      setSplash(false);
      locationState.current = null;
      const observerAppInstance = sdk.getObserverAppInstance();

      if (!sdk.getFirebase().isLoggedIn()) {
        observerAppInstance.trigger("usercreate:show");
      }

      observerAppInstance
        .attach("usercreate:done", async (shouldUpdate: any) => {
          if (sdk.getFirebase().isLoggedIn()) {
            navigate("/game");
          } else {
            setSplash(true);
          }
        });
      observerAppInstance.attach("userscreen:transition:signup_to_login", async () => onLogin());
    }

    const onForgotPassword = () => {
      setSplash(false);
      locationState.current = null;

      if (!sdk.getFirebase().isLoggedIn()) {
        sdk.getObserverAppInstance().trigger("userforgotpassword:show");
      }

      const observerAppInstance = sdk.getObserverAppInstance()
      observerAppInstance.attach("userscreen:transition:forgotpassword_to_signup", async () => onSignUp());
      observerAppInstance.attach("userscreen:modal:close", async () => setSplash(true));
    }
    return { onLogin, onSignUp };
  }, [navigate, sdk, setSplash])

  useEffect(() => {
    if (sdk && sdk.getFirebase().isLoggedIn()) setIsLoggedIn(true);
    if (locationState.current?.instantLogin) onLogin();
    if (locationState.current?.instantRegister) onSignUp();
  }, [sdk, locationState, onLogin, onSignUp, navigate]);

  return (
    <div className="exercise-screen full-screen">
      <Box
        sx={{
          width: "100%",
          mt: isLargeScreen ? "12.6rem" : "10rem",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          rowGap: "24px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Text fontSize={isLargeScreen ? "30px" : "24"} textAlign="center">
            <FormattedMessage id="splash-screen-pre-heading" defaultMessage={`Exercice interactif`} />
          </Text>
          <Text fontSize={isLargeScreen ? "40px" : "32px"} fontWeight={900} textAlign="center">
            <FormattedMessage id="splash-screen-heading" defaultMessage={`Operations sur les nombres décimaux`} />
          </Text>
        </Box>
        <Text fontSize={isLargeScreen ? "20px" : "16px"} fontWeight={600} textAlign="center">
          <FormattedMessage id="splash-screen-sub-heading" defaultMessage={`Mathématique Prim. 3 - Sec. 1`} />
        </Text>
        <Text fontSize={isLargeScreen ? "20px" : "16px"} textAlign="center">
          <FormattedMessage
            id="splash-screen-description"
            defaultMessage={`Cursus aliquet id feugiat proin maecenas feugiat neque vitae.`}
          />
        </Text>
        <Box>
          <FullScreenContext.Consumer>
            {({ toggleFullScreen }) => (
              <APButton
                colorScheme={"primary"}
                size="lg"
                onClick={() => {
                  const appUrl = process.env.REACT_APP_URL
                  const isInIframe = () => window.self !== window.top
                  try {
                    isIOS && isInIframe() ?
                      window.open(appUrl, '_blank', 'noreferrer') :
                      !isLargeScreen && toggleFullScreen()
                  } catch (err) {}
                  isLoggedIn ? navigate("/game", { replace: true }) : setSplash(true)
                }}
              >
                <FormattedMessage id="start-button" defaultMessage={`Commencer`}/>
              </APButton>
            )}
          </FullScreenContext.Consumer>
        </Box>
      </Box>
      <Modal
        closeOnOverlayClick={false}
        isOpen={splash}
        onClose={() => {}}
        size="sm"
      >
        <ModalOverlay>
          <ModalContent sx={{maxWidth: "483px"}}>
            <ModalBody className="ap-dialog-body">
              <Box
                sx={{
                  background: "#F4F5A0",
                  borderRadius: "20px 20px 0px 0px",
                  textAlign: "center",
                  padding: isLargeScreen ? "38px 38px 0px 38px" : "30px 30px 0px 30px",
                  color: "#000",
                }}
              >
                <Box
                  sx={{
                    lineHeight: "1.3",
                    fontSize: isLargeScreen ? "26px" : "21px",
                    fontWeight: "bold",
                    mb: "2",
                  }}
                >
                  <FormattedMessage
                    id="splash-login-heading"
                    defaultMessage={`Connecte-toi pour profiter d’une expérience unique!`}
                  />
                </Box>
                <Box className="ap-dialog-head-img">
                  <Image
                    src={`${process.env.REACT_APP_BASE_URL}/success.png`}
                    sx={{
                      width: isLargeScreen ? "315px" : "250px",
                      height: isLargeScreen ? "226px" : "180px",
                      marginTop: isLargeScreen ? "49px" : "39px",
                    }}
                  />
                </Box>
              </Box>
            </ModalBody>

            <ModalFooter
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                width: "100%",
                p: isLargeScreen ? "20px 30px" : "16px 24px"
              }}
            >
              <APButton
                onClick={() => onLogin()}
                colorScheme="primary"
                sx={{w: isLargeScreen ? "378px" : "300px"}}
              >
                <FormattedMessage id="login" defaultMessage={"Se connecter"} />
              </APButton>

              <APButton
                variant={"solid"}
                sx={{
                  w: isLargeScreen ? "378px" : "300px",
                  mt: isLargeScreen ? "6" : "5",
                  fontWeight: "500",
                }}
                onClick={() => onSignUp()}
              >
                <FormattedMessage
                  id="create-account"
                  defaultMessage={"Créer mon compte"}
                />
              </APButton>

              <Button
                variant={"solid"}
                sx={{
                  bg: "white",
                  color: "black",
                  "&:hover": {
                    bg: "white",
                  },
                  pa: "0",
                  mt: isLargeScreen ? "4" : "3",
                  textDecoration: "underline",
                  fontSize: isLargeScreen ? "18px" : "14px",
                }}
                onClick={() => {
                  navigate("/game?demo=1", { replace: true });
                }}
              >
                <FormattedMessage
                  id="continue-without-login"
                  defaultMessage={"Continuer sans se connecter"}
                />
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </div>
  );
}
